/**
 * F42 (Wave 2 — B2) — ClaimsTab data + action surface.
 *
 * Split out from `ClaimsTab.tsx` so the component file only exports components
 * (react-refresh/only-export-components). The default wires the venue-scoped
 * reads/decisions to the F11 admin claim client; tests inject a deterministic
 * fake instead. The list is forward-compatible with the A3 server-side
 * `?venueId=` filter and falls back to client-side filtering.
 */
import type { VenueClaim, VenueClaimDocument } from '@bravemove/types';
import { adminFetch, buildQuery } from '../../../auth/apiClient.js';
import { adminVenueClaimApi } from '../../../api/venue-claim.js';

export interface VenueClaimWithDocs {
  readonly claim: VenueClaim;
  readonly documents: readonly VenueClaimDocument[];
  readonly claimantEmail: string | null;
}

export interface ClaimsTabApi {
  readonly listForVenue: (venueId: string) => Promise<readonly VenueClaimWithDocs[]>;
  readonly approve: (claimId: string) => Promise<void>;
  readonly reject: (claimId: string, reason: string) => Promise<void>;
  readonly revoke: (claimId: string, reason: string) => Promise<void>;
  readonly getDocumentDownloadUrl: (
    claimId: string,
    docId: string,
    reason?: string,
  ) => Promise<{ url: string; expiresAt: string }>;
}

interface VenueClaimQueueRowLike {
  readonly id: string;
  readonly venue: { readonly id: string } | null;
  readonly claimant: { readonly email: string } | null;
}

/** Default API — wires the venue-scoped reads/decisions to the F11 admin client. */
export const defaultClaimsTabApi: ClaimsTabApi = {
  async listForVenue(venueId) {
    const res = await adminFetch<{ items: readonly VenueClaimQueueRowLike[] }>(
      `/admin/venue-claims${buildQuery({ venueId, limit: 100 })}`,
    );
    const rows = (res.items ?? []).filter((r) => !r.venue || r.venue.id === venueId);
    const detailed = await Promise.all(
      rows.map(async (row) => {
        const detail = await adminVenueClaimApi.detail(row.id);
        return {
          claim: detail.claim,
          documents: detail.documents,
          claimantEmail: detail.claimant?.email ?? null,
        };
      }),
    );
    return detailed;
  },
  async approve(claimId) {
    await adminVenueClaimApi.approve(claimId, {});
  },
  async reject(claimId, reason) {
    await adminVenueClaimApi.reject(claimId, { reason });
  },
  async revoke(claimId, reason) {
    await adminVenueClaimApi.revoke(claimId, { reason });
  },
  getDocumentDownloadUrl(claimId, docId, reason) {
    return adminVenueClaimApi.getDocumentDownloadUrl(claimId, docId, reason);
  },
};
